import { findRequisitionReplacement, pickCurrentByResultKey } from './labOverviewCurrent';
import { isPreliminaryStatus } from './labVersioning';
import type {
  LabAntibiotic,
  LabInvestigation,
  LabInvestigationVersion,
  LabMicroscopy,
  LabOrganism,
  LabResistance,
  LabReportRow,
  LabResultRow,
} from './labTypes';

export function compareDisplayOrder(
  a: Pick<LabResultRow, 'display_order' | 'analysis_code'>,
  b: Pick<LabResultRow, 'display_order' | 'analysis_code'>,
): number {
  const ao = a.display_order;
  const bo = b.display_order;
  if (ao != null && bo != null && ao !== bo) return ao - bo;
  if (ao != null && bo == null) return -1;
  if (ao == null && bo != null) return 1;
  return (a.analysis_code ?? '').localeCompare(b.analysis_code ?? '');
}

export function reportCardItems(
  reportId: string,
  data: { items?: LabResultRow[] | null; current: LabResultRow[] },
): LabResultRow[] {
  const source = Array.isArray(data.items)
    ? data.items.filter((row) => row.report_id === reportId)
    : data.current.filter((row) => row.report_id === reportId);
  return source.slice().sort(compareDisplayOrder);
}

export function currentByResultKey(current: LabResultRow[]): Map<string, LabResultRow> {
  const map = new Map<string, LabResultRow>();
  for (const row of current) {
    if (!map.has(row.result_key)) map.set(row.result_key, row);
  }
  return map;
}

export function isSupersededLabItem(
  item: Pick<LabResultRow, 'report_id'>,
  current: Pick<LabResultRow, 'report_id'> | undefined,
): boolean {
  return Boolean(current && current.report_id !== item.report_id);
}

export type ReplacementKind = 'rettet_svar' | 'endeligt_svar' | 'svar';

export function replacementKind(
  current: Pick<LabResultRow, 'result_status_code'> | { result_status_code?: string | null },
  superseded?: { result_status_code?: string | null } | null,
): ReplacementKind {
  if (current.result_status_code === 'svar_rettet') return 'rettet_svar';
  if (current.result_status_code === 'svar_endeligt' && isPreliminaryStatus(superseded?.result_status_code)) {
    return 'endeligt_svar';
  }
  return 'svar';
}

export type LabReplacementPointer = {
  result_key: string;
  hidden?: boolean;
  report_id: string | null;
  results_datetime: string | null;
  result_status_code: string | null;
};

export type ItemReplacementMark =
  | { hidden: true }
  | { hidden: false; kind: ReplacementKind; results_datetime: string | null };

function markFromReplacement(
  item: { report_id: string; result_status_code?: string | null },
  hit: LabReplacementPointer,
): ItemReplacementMark | null {
  if (hit.hidden || !hit.report_id || hit.report_id === item.report_id) {
    return hit.hidden ? { hidden: true } : null;
  }
  return {
    hidden: false,
    kind: replacementKind(hit, item),
    results_datetime: hit.results_datetime,
  };
}

export function replacementForItem(
  item: Pick<LabResultRow, 'report_id' | 'result_key' | 'result_status_code'>,
  data: { current: LabResultRow[]; replacements?: LabReplacementPointer[] | null },
): ItemReplacementMark | null {
  if (Array.isArray(data.replacements)) {
    const hit = data.replacements.find((row) => row.result_key === item.result_key);
    if (!hit) return null;
    return markFromReplacement(item, hit);
  }
  const current = currentByResultKey(data.current).get(item.result_key);
  if (!isSupersededLabItem(item, current) || !current) return null;
  return {
    hidden: false,
    kind: replacementKind(current, item),
    results_datetime: current.results_datetime,
  };
}

export function replacementForInvestigation(
  item: Pick<LabInvestigation, 'report_id' | 'result_key' | 'result_status_code' | 'mic_analysis_code'>,
  data: {
    investigations: LabInvestigation[];
    replacements?: LabReplacementPointer[] | null;
    reports?: Array<Pick<LabReportRow, 'id' | 'requester_sample_id'>> | null;
  },
): ItemReplacementMark | null {
  if (Array.isArray(data.replacements)) {
    const hit = data.replacements.find((row) => row.result_key === item.result_key);
    if (hit) return markFromReplacement(item, hit);
  }
  const current = pickCurrentByResultKey(data.investigations).get(item.result_key);
  if (current && isSupersededLabItem(item, current)) {
    return {
      hidden: false,
      kind: replacementKind(current, item),
      results_datetime: current.results_datetime,
    };
  }
  const winner = findRequisitionReplacement(item, data.investigations, data.reports ?? []);
  if (!winner || winner.report_id === item.report_id) return null;
  return {
    hidden: false,
    kind: replacementKind(winner, item),
    results_datetime: winner.results_datetime,
  };
}

export type LetterMicroBundle = {
  investigations: LabInvestigation[];
  microscopy: LabMicroscopy[];
  organisms: LabOrganism[];
  antibiotics: LabAntibiotic[];
  resistance: LabResistance[];
};

type VersionWithSnapshot = LabInvestigationVersion & { snapshot?: unknown };

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function textField(row: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}

function numberField(row: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

function investigationFromSnapshot(
  reportId: string,
  version: VersionWithSnapshot,
  snapshot: Record<string, unknown>,
): LabInvestigation {
  return {
    id: `letter:${reportId}:${version.id}`,
    report_id: reportId,
    result_key: textField(snapshot, 'result_key', 'resultKey') ?? '',
    examination_type_code: textField(snapshot, 'examination_type_code', 'examinationTypeCode'),
    mic_analysis_code: textField(snapshot, 'mic_analysis_code', 'micAnalysisCode'),
    analysis_code_type: textField(snapshot, 'analysis_code_type', 'analysisCodeType'),
    analysis_code_responsible: textField(snapshot, 'analysis_code_responsible', 'analysisCodeResponsible'),
    analysis_short_name: textField(snapshot, 'analysis_short_name', 'analysisShortName'),
    analysis_mds_examination: textField(snapshot, 'analysis_mds_examination', 'analysisMdsExamination'),
    analysis_mds_material: textField(snapshot, 'analysis_mds_material', 'analysisMdsMaterial'),
    analysis_mds_location: textField(snapshot, 'analysis_mds_location', 'analysisMdsLocation'),
    supplementary_location: textField(snapshot, 'supplementary_location', 'supplementaryLocation'),
    examinator: textField(snapshot, 'examinator'),
    producer_identifier: textField(snapshot, 'producer_identifier', 'producerIdentifier'),
    producer_identifier_code: textField(snapshot, 'producer_identifier_code', 'producerIdentifierCode'),
    examination_summary:
      textField(snapshot, 'examination_summary', 'examinationSummary') ?? version.examination_summary,
    result_status_code: textField(snapshot, 'result_status_code', 'resultStatusCode') ??
      version.result_status_code,
    results_datetime: textField(snapshot, 'results_datetime', 'resultsDateTime') ?? version.results_datetime,
    quantitative: asRecord(snapshot.quantitative) ?? {},
    susceptibility_interpretation: textField(
      snapshot,
      'susceptibility_interpretation',
      'susceptibilityInterpretation',
    ),
    culture_headline: textField(snapshot, 'culture_headline', 'cultureHeadline'),
    pattern_headline: textField(snapshot, 'pattern_headline', 'patternHeadline') ?? undefined,
    culture_comments: textField(snapshot, 'culture_comments', 'cultureComments'),
  };
}

function microscopyFromSnapshot(investigationId: string, snapshot: Record<string, unknown>): LabMicroscopy | null {
  const raw = snapshot.microscopy;
  const row = Array.isArray(raw) ? asRecord(raw[0]) : asRecord(raw);
  if (!row) return null;
  const groupings = Array.isArray(row.groupings) ? row.groupings : [];
  return {
    id: `${investigationId}:micro`,
    investigation_id: investigationId,
    headline: textField(row, 'headline'),
    comments: textField(row, 'comments'),
    groupings,
  };
}

function organismsFromSnapshot(investigationId: string, snapshot: Record<string, unknown>): LabOrganism[] {
  const raw = snapshot.organisms;
  if (!Array.isArray(raw)) return [];
  return raw.map((entry, index) => {
    const row = asRecord(entry) ?? {};
    return {
      id: `${investigationId}:org:${index}`,
      investigation_id: investigationId,
      ordinal: numberField(row, 'ordinal') || index + 1,
      identification_text: textField(row, 'identification_text', 'identificationText'),
      code: textField(row, 'code'),
      code_type: textField(row, 'code_type', 'codeType'),
      code_responsible: textField(row, 'code_responsible', 'codeResponsible'),
      growth_value: textField(row, 'growth_value', 'growthValue'),
      species_comment: textField(row, 'species_comment', 'speciesComment'),
    };
  });
}

function antibioticsFromSnapshot(investigationId: string, snapshot: Record<string, unknown>): LabAntibiotic[] {
  const raw = snapshot.antibiotics;
  if (!Array.isArray(raw)) return [];
  return raw.map((entry, index) => {
    const row = asRecord(entry) ?? {};
    return {
      id: `${investigationId}:ab:${index}`,
      investigation_id: investigationId,
      ordinal: numberField(row, 'ordinal') || index + 1,
      name: textField(row, 'name'),
    };
  });
}

function resistanceFromSnapshot(investigationId: string, snapshot: Record<string, unknown>): LabResistance[] {
  const raw = snapshot.resistance;
  if (!Array.isArray(raw)) return [];
  return raw.map((entry, index) => {
    const row = asRecord(entry) ?? {};
    return {
      id: `${investigationId}:r:${index}`,
      investigation_id: investigationId,
      ref_microorganism: numberField(row, 'ref_microorganism', 'refMicroorganism'),
      ref_antibiotic: numberField(row, 'ref_antibiotic', 'refAntibiotic'),
      susceptibility: textField(row, 'susceptibility'),
      susceptibility_value: textField(row, 'susceptibility_value', 'susceptibilityValue'),
      susceptibility_operator: textField(row, 'susceptibility_operator', 'susceptibilityOperator'),
      susceptibility_unit: textField(row, 'susceptibility_unit', 'susceptibilityUnit'),
      susceptibility_references: Array.isArray(row.susceptibility_references)
        ? row.susceptibility_references
        : Array.isArray(row.references)
          ? row.references
          : [],
    };
  });
}

/** This letter's XRPT05 investigations — not the current row of a later 2.6_A. */
export function reportCardMicro(
  reportId: string,
  data: {
    investigations: LabInvestigation[];
    microscopy: LabMicroscopy[];
    organisms: LabOrganism[];
    antibiotics: LabAntibiotic[];
    resistance: LabResistance[];
    investigation_versions?: VersionWithSnapshot[] | null;
  },
): LetterMicroBundle {
  const owned = data.investigations.filter((inv) => inv.report_id === reportId);
  if (owned.length > 0) {
    const ids = new Set(owned.map((inv) => inv.id));
    return {
      investigations: owned,
      microscopy: data.microscopy.filter((row) => ids.has(row.investigation_id)),
      organisms: data.organisms.filter((row) => ids.has(row.investigation_id)),
      antibiotics: data.antibiotics.filter((row) => ids.has(row.investigation_id)),
      resistance: data.resistance.filter((row) => ids.has(row.investigation_id)),
    };
  }
  const versions = (data.investigation_versions ?? []).filter(
    (version) => version.report_id === reportId && version.snapshot,
  );
  const investigations: LabInvestigation[] = [];
  const microscopy: LabMicroscopy[] = [];
  const organisms: LabOrganism[] = [];
  const antibiotics: LabAntibiotic[] = [];
  const resistance: LabResistance[] = [];
  for (const version of versions) {
    const snapshot = asRecord(version.snapshot);
    if (!snapshot) continue;
    const inv = investigationFromSnapshot(reportId, version, snapshot);
    investigations.push(inv);
    const micro = microscopyFromSnapshot(inv.id, snapshot);
    if (micro) microscopy.push(micro);
    organisms.push(...organismsFromSnapshot(inv.id, snapshot));
    antibiotics.push(...antibioticsFromSnapshot(inv.id, snapshot));
    resistance.push(...resistanceFromSnapshot(inv.id, snapshot));
  }
  return { investigations, microscopy, organisms, antibiotics, resistance };
}
